import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Image,
  ScrollView,
  Alert,
  ActivityIndicator,
} from 'react-native';
import { ms, s, vs } from '../../utils/responsive';
import { SafeAreaView } from 'react-native-safe-area-context';
import { onboardingService } from '../../services/onboarding';
import { analytics } from '../../services/analytics/analytics';
import { AnalyticsEvent } from '../../services/analytics/events';

import { createLogger } from '../../utils/logger';
const logger = createLogger('AIOnboardingWelcome');

export const AIOnboardingWelcomeScreen = ({ navigation }: any) => {
  const [isSkipping, setIsSkipping] = useState(false);

  useEffect(() => {
    analytics.track(AnalyticsEvent.AiOnboardingWelcomeShown);
  }, []);

  const handleQuickStart = () => {
    analytics.track(AnalyticsEvent.AiOnboardingModeSelected, { mode: 'quick_start' });
    navigation.navigate('AIOnboardingTutorial', { mode: 'quick_start' });
  };

  const handleComprehensive = () => {
    analytics.track(AnalyticsEvent.AiOnboardingModeSelected, { mode: 'comprehensive' });
    navigation.navigate('AIOnboardingTutorial', { mode: 'comprehensive' });
  };

  const handleSkip = () => {
    Alert.alert(
      'Skip Onboarding?',
      'You can always complete onboarding later from Settings. Your tasks will be prioritized based on defaults.',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Skip',
          style: 'destructive',
          onPress: async () => {
            analytics.track(AnalyticsEvent.AiOnboardingSkipped, { from: 'welcome' });
            setIsSkipping(true);
            try {
              await onboardingService.skipOnboarding();
              // Navigate to Dashboard after skipping
              navigation.reset({
                index: 0,
                routes: [{ name: 'Dashboard' }],
              });
            } catch (error) {
              logger.error('Failed to skip onboarding:', error);
              // Still navigate even if API fails - user wants to skip
              navigation.reset({
                index: 0,
                routes: [{ name: 'Dashboard' }],
              });
            } finally {
              setIsSkipping(false);
            }
          },
        },
      ]
    );
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        {navigation.canGoBack() ? (
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Image
              source={require('../../assets/icons/back.png')}
              style={styles.backIcon}
            />
          </TouchableOpacity>
        ) : (
          <View style={styles.backButton} />
        )}
        <TouchableOpacity onPress={handleSkip} disabled={isSkipping}>
          {isSkipping ? (
            <ActivityIndicator size="small" color="#666" />
          ) : (
            <Text style={styles.skipText}>Skip</Text>
          )}
        </TouchableOpacity>
      </View>

      <ScrollView
        contentContainerStyle={styles.content}
        showsVerticalScrollIndicator={false}
      >
        {/* Orion Avatar */}
        <View style={styles.avatarContainer}>
          <View style={styles.avatar}>
            <Image
              source={require('../../assets/icons/chat.png')}
              style={styles.avatarImage}
            />
          </View>
        </View>

        {/* Welcome Message */}
        <Text style={styles.title}>Hi! I'm Orion</Text>
        <Text style={styles.description}>
          I'm here to help you get started with FinalKey. I'll ask you a few
          questions to understand your situation and prioritize what's most
          important for you.
        </Text>

        {/* Mode Selection Cards */}
        <View style={styles.modesContainer}>
          <TouchableOpacity
            style={[styles.modeCard, styles.quickStartCard]}
            onPress={handleQuickStart}
            activeOpacity={0.8}
          >
            <View style={styles.modeHeader}>
              <Text style={styles.modeTitle}>Quick Start</Text>
              <Text style={styles.arrowIcon}>{'>>'}</Text>
            </View>
            <Text style={styles.modeTime}>~5 minutes</Text>
            <Text style={styles.modeDescription}>
              Answer 5 essential questions to get your personalized priorities
            </Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={[styles.modeCard, styles.comprehensiveCard]}
            onPress={handleComprehensive}
            activeOpacity={0.8}
          >
            <View style={styles.modeHeader}>
              <Text style={styles.modeTitle}>Comprehensive</Text>
              <Text style={styles.arrowIcon}>{'>>'}</Text>
            </View>
            <Text style={styles.modeTime}>~15 minutes</Text>
            <Text style={styles.modeDescription}>
              Detailed conversation for complete personalized planning
            </Text>
          </TouchableOpacity>
        </View>

        <Text style={styles.noteText}>
          You can pause and resume anytime, or skip for now and complete later.
        </Text>
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F3F6F8',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: ms(16),
    paddingVertical: ms(12),
    paddingRight: ms(80),
  },
  backButton: {
    padding: ms(8),
  },
  backIcon: {
    width: s(24),
    height: s(24),
    tintColor: '#1C2C4F',
  },
  skipText: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#666',
  },
  content: {
    paddingHorizontal: ms(24),
    paddingTop: ms(20),
    paddingBottom: ms(40),
  },
  avatarContainer: {
    alignItems: 'center',
    marginBottom: ms(24),
  },
  avatar: {
    width: s(100),
    height: s(100),
    borderRadius: ms(50),
    backgroundColor: '#29BFC0',
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  avatarImage: {
    width: s(60),
    height: s(60),
  },
  title: {
    fontSize: ms(28, 0.3),
    fontWeight: '700',
    color: '#1C2C4F',
    textAlign: 'center',
    marginBottom: ms(16),
  },
  description: {
    fontSize: ms(16, 0.3),
    color: '#666',
    textAlign: 'center',
    lineHeight: ms(24, 0.3),
    marginBottom: ms(40),
  },
  modesContainer: {
    gap: ms(16),
    marginBottom: ms(24),
  },
  modeCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(20),
    padding: ms(24),
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 8,
    elevation: 3,
  },
  quickStartCard: {
    borderLeftWidth: ms(4),
    borderLeftColor: '#29BFC0',
  },
  comprehensiveCard: {
    borderLeftWidth: ms(4),
    borderLeftColor: '#6B7280',
  },
  modeHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: ms(8),
  },
  modeTitle: {
    fontSize: ms(20, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
  },
  arrowIcon: {
    fontSize: ms(18, 0.3),
    fontWeight: '600',
    color: '#29BFC0',
  },
  modeTime: {
    fontSize: ms(14, 0.3),
    fontWeight: '600',
    color: '#29BFC0',
    marginBottom: ms(8),
  },
  modeDescription: {
    fontSize: ms(14, 0.3),
    color: '#666',
    lineHeight: ms(20, 0.3),
  },
  noteText: {
    fontSize: ms(14, 0.3),
    color: '#999',
    textAlign: 'center',
    fontStyle: 'italic',
    lineHeight: ms(20, 0.3),
  },
});
